import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import cmsApi from "../../services/cmsApi";

// Default cards data (constant, never changes)
const defaultCards = [
  {
    id: 1,
    iconSvg: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
    title: "Short Courses",
    borderColor: "#FFC72C",
    features: [
      "Computer Applications",
      "Graphics Design",
      "Web Design & Development",
      "Languages & Communication",
    ],
    buttonText: "Start Learning Today",
    buttonLink: "/course/short-courses",
  },
  {
    id: 2,
    iconSvg: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
    title: "Diploma Courses",
    borderColor: "#E53E73",
    features: [
      "Information Technology",
      "Cybersecurity",
      "Business Administration",
      "Travel & Tourism Management",
    ],
    buttonText: "Enroll Now",
    buttonLink: "/admissions",
  },
  {
    id: 3,
    iconSvg: "M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422A12.083 12.083 0 0112 21.5 12.083 12.083 0 015.84 10.578L12 14z",
    title: "Professional Courses",
    borderColor: "#6366F1",
    features: [
      "CompTIA Certification",
      "Cisco Networking Academy",
      "EC-Council Certification",
    ],
    buttonText: "Explore Professional Courses",
    buttonLink: "/courses/professional-courses",
  },
];

const StatisticsSection = () => {
  const [sectionData, setSectionData] = useState({
    title: "Choose Your",
    titleHighlight: "Success Path",
    subtitle: "Transform your future with our comprehensive programs designed for career excellence",
    footerText: "Join thousands of successful graduates who chose JR Institute for their professional journey",
    footerBullets: ["NACTVET Accredited", "Industry Partnerships", "Expert Faculty"],
    cards: defaultCards,
  });

  // Fetch statistics content from CMS (client-side only)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const fetchStatisticsContent = async () => {
      try {
        const result = await cmsApi.getHomepageComponentByType("statistics");
        if (result.success && result.components.length > 0) {
          const statistics = result.components[0];
          const settings = statistics.settings || {};

          // Get title and titleHighlight from CMS
          const title = statistics.title || "Choose Your";
          const titleHighlight = settings.titleHighlight || "Success Path";

          setSectionData({
            title: title,
            titleHighlight: titleHighlight,
            subtitle:
              statistics.subtitle ||
              "Transform your future with our comprehensive programs designed for career excellence",
            footerText:
              statistics.content ||
              "Join thousands of successful graduates who chose JR Institute for their professional journey",
            footerBullets:
              settings.footerBullets || [
                "NACTVET Accredited",
                "Industry Partnerships",
                "Expert Faculty",
              ],
            cards: settings.statisticsCards || defaultCards,
          });
        }
      } catch (error) {
        console.log("Using default statistics content:", error.message);
      }
    };

    fetchStatisticsContent();
  }, []);

  return (
    <section className="statistics-section bg-gradient-to-br from-custom-pink via-purple-800 to-indigo-900 py-16 sm:py-20 lg:py-24 relative overflow-hidden min-[1024px]:max-[1535px]:!py-12">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-net-pattern"></div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-gold via-yellow-400 to-gold"></div>
      <div className="absolute bottom-0 right-0 w-32 h-32 bg-gold opacity-20 rounded-full transform translate-x-16 translate-y-16"></div>
      <div className="absolute top-1/4 left-0 w-24 h-24 bg-white opacity-10 rounded-full transform -translate-x-12"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 min-[1024px]:max-[1535px]:!px-4">
        {/* Header */}
        <div className="text-center mb-12 min-[1024px]:max-[1535px]:!mb-8">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 min-[1024px]:max-[1535px]:!text-3xl min-[1024px]:max-[1535px]:!mb-3">
            {sectionData.title}
            <span className="block text-gold">{sectionData.titleHighlight}</span>
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto min-[1024px]:max-[1535px]:!text-base">
            {sectionData.subtitle}
          </p>
        </div>

        {/* Program Cards */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto min-[1024px]:max-[1535px]:!gap-6">
          {sectionData.cards.map((card, index) => {
            const isThirdCard = index === 2;
            const borderColor = card.borderColor || "#FFC72C";
            const isExternalLink = card.buttonLink?.startsWith("http");

            return (
              <div
                key={card.id || index}
                className={isThirdCard ? "md:col-span-2 flex justify-center" : "group relative"}
              >
                <div
                  className={`bg-white rounded-2xl p-8 lg:p-10 shadow-2xl transform group-hover:scale-105 transition-all duration-300 border-l-4 min-[1024px]:max-[1535px]:!rounded-xl min-[1024px]:max-[1535px]:!p-6 min-[1024px]:max-[1535px]:!lg:p-6 ${
                    isThirdCard ? "w-full max-w-2xl" : ""
                  }`}
                  style={{ borderLeftColor: borderColor }}
                >
                  {/* Icon */}
                  <div
                    className="flex items-center justify-center w-16 h-16 rounded-full mb-6 mx-auto group-hover:rotate-12 transition-transform duration-300 min-[1024px]:max-[1535px]:!w-12 min-[1024px]:max-[1535px]:!h-12 min-[1024px]:max-[1535px]:!mb-4"
                    style={{
                      background: `linear-gradient(to bottom right, ${borderColor}, ${borderColor}dd)`,
                    }}
                  >
                    <svg
                      className="w-8 h-8 text-white min-[1024px]:max-[1535px]:!h-6 min-[1024px]:max-[1535px]:!w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={card.iconSvg}
                      />
                    </svg>
                  </div>

                  <h3 className="text-3xl lg:text-4xl font-bold text-custom-pink mb-4 text-center min-[1024px]:max-[1535px]:!text-2xl min-[1024px]:max-[1535px]:!mb-3">
                    {card.title}
                  </h3>

                  <div className="space-y-4 mb-8 min-[1024px]:max-[1535px]:!space-y-2 min-[1024px]:max-[1535px]:!mb-6">
                    {Array.isArray(card.features) &&
                      card.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-gray-700">
                          <svg
                            className="w-5 h-5 mr-3 flex-shrink-0"
                            style={{ color: borderColor }}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="font-semibold min-[1024px]:max-[1535px]:!text-sm">{feature}</span>
                        </div>
                      ))}
                  </div>

                  <div className="text-center">
                    {isExternalLink ? (
                      <a
                        href={card.buttonLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block text-white px-8 py-3 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-shadow duration-300 min-[1024px]:max-[1535px]:!px-5 min-[1024px]:max-[1535px]:!py-2 min-[1024px]:max-[1535px]:!text-sm"
                        style={{
                          background: `linear-gradient(to right, ${borderColor}, ${borderColor}dd)`,
                        }}
                      >
                        {card.buttonText}
                      </a>
                    ) : (
                      <Link
                        to={card.buttonLink || "#"}
                        className="inline-block text-white px-8 py-3 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-shadow duration-300 min-[1024px]:max-[1535px]:!px-5 min-[1024px]:max-[1535px]:!py-2 min-[1024px]:max-[1535px]:!text-sm"
                        style={{
                          background: `linear-gradient(to right, ${borderColor}, ${borderColor}dd)`,
                        }}
                      >
                        {card.buttonText}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 min-[1024px]:max-[1535px]:!mt-8">
          <p className="text-white text-lg mb-6 min-[1024px]:max-[1535px]:!text-base min-[1024px]:max-[1535px]:!mb-4">
            {sectionData.footerText}
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-200 min-[1024px]:max-[1535px]:!gap-3 min-[1024px]:max-[1535px]:!text-xs">
            {Array.isArray(sectionData.footerBullets) &&
              sectionData.footerBullets.map((bullet, index) => (
                <span key={index} className="flex items-center">
                  <svg
                    className="w-4 h-4 text-gold mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {bullet}
                </span>
              ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatisticsSection;
